// src/components/NewAudioComponentForm.js
import React, { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebaseConfig';

export default function NewAudioComponentForm({
                                                  userId,

                                                  // title/description props + callbacks
                                                  title = '',
                                                  description = '',
                                                  onTitleChange,
                                                  onDescriptionChange,

                                                  // final callback (audioUrl, coverUrl, title, description, gate) => void
                                                  onUploadComplete,

                                                  // NEW props for 3-button footer
                                                  enablePublishButton = false,
                                                  onSubmitAndPublish,

                                                  onCancel,
                                                  error,
                                              }) {
    const [audioFile, setAudioFile] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [localError, setLocalError] = useState('');

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB audio
    const MAX_COVER_SIZE = 50 * 1024 * 1024; // 50MB image

    // Upload both files, resolve a full payload object
    const performUpload = () =>
        new Promise(async (resolve, reject) => {
            try {
                setLocalError('');
                if (!audioFile || !coverFile) {
                    setLocalError('Please select both a cover image and an audio file.');
                    return reject(new Error('missing-files'));
                }

                if (coverFile.size > MAX_COVER_SIZE) {
                    const msg = `Cover image too large. Max ${MAX_COVER_SIZE / 1024 / 1024} MB.`;
                    alert(msg);
                    setLocalError(msg);
                    return reject(new Error('cover-too-large'));
                }
                if (audioFile.size > MAX_FILE_SIZE) {
                    const msg = `Audio file too large. Max ${MAX_FILE_SIZE / 1024 / 1024} MB.`;
                    alert(msg);
                    setLocalError(msg);
                    return reject(new Error('audio-too-large'));
                }

                setUploading(true);

                const uid = userId || auth.currentUser?.uid || 'anonymous';
                const stamp = Date.now();

                // 1) Upload cover
                const coverPath = `images/${uid}/${stamp}_${coverFile.name}`;
                const coverRef = ref(storage, coverPath);
                const coverTask = uploadBytesResumable(coverRef, coverFile);

                coverTask.on(
                    'state_changed',
                    () => {},
                    (err) => {
                        console.error(err);
                        setUploading(false);
                        setLocalError('Cover upload failed.');
                        reject(err);
                    },
                    async () => {
                        const coverUrl = await getDownloadURL(coverTask.snapshot.ref);

                        // 2) Upload audio
                        const audioPath = `audio/${uid}/${stamp}_${audioFile.name}`;
                        const audioRef = ref(storage, audioPath);
                        const audioTask = uploadBytesResumable(audioRef, audioFile);

                        audioTask.on(
                            'state_changed',
                            () => {},
                            (err) => {
                                console.error(err);
                                setUploading(false);
                                setLocalError('Audio upload failed.');
                                reject(err);
                            },
                            async () => {
                                const audioUrl = await getDownloadURL(audioTask.snapshot.ref);
                                setUploading(false);

                                resolve({
                                    audioUrl,
                                    coverUrl,
                                    title,
                                    description,
                                });
                            }
                        );
                    }
                );
            } catch (e) {
                console.error(e);
                setUploading(false);
                setLocalError('Upload failed.');
                reject(e);
            }
        });

    const handleUpload = async () => {
        try {
            const { audioUrl, coverUrl, title: t, description: d, gate } = await performUpload();
            onUploadComplete(audioUrl, coverUrl, t ?? title, d ?? description, gate);
        } catch {
            /* alerts/localError already set */
        }
    };

    const handleUploadAndPublish = async () => {
        try {
            const { audioUrl, coverUrl, title: t, description: d, gate } = await performUpload();
            // Save + Publish path (parent wires this to save then call handleSaveMuulti)
            onSubmitAndPublish?.(audioUrl, coverUrl, t ?? title, d ?? description, gate);
        } catch {
            /* alerts/localError already set */
        }
    };

    return (
        <div className="new-component-form audio-form">
            <h1>Audio Component Form</h1>

            <input
                type="text"
                placeholder="Audio Title"
                value={title}
                onChange={(e) => onTitleChange?.(e.target.value)}
                className="muulti-input"
            />

            <textarea
                placeholder="Audio Description"
                value={description}
                onChange={(e) => onDescriptionChange?.(e.target.value)}
                className="muulti-textarea"
            />

            <h3>Select a cover image</h3>
            <input
                type="file"
                accept="image/*"
                onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)}
                className="muulti-input"
            />

            <h3>Select an audio file</h3>
            <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files?.[0] ?? null)}
                className="muulti-input"
            />

            <div className="form-buttons">
                <button
                    onClick={handleUpload}
                    disabled={!coverFile || !audioFile || uploading}
                    className="muulti-button"
                >
                    {uploading ? 'Uploading…' : 'Save Panel'}
                </button>

                {enablePublishButton && (
                    <button
                        onClick={handleUploadAndPublish}
                        disabled={!coverFile || !audioFile || uploading}
                        className="muulti-button"
                    >
                        {uploading ? 'Uploading…' : 'Save Panel & Publish'}
                    </button>
                )}

                <button onClick={onCancel} className="muulti-button cancel-button">
                    Cancel
                </button>
            </div>

            {(localError || error) && <p className="error">{localError || error}</p>}
        </div>
    );
}
